// Core
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { AnchorNav, Button, Chip } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import VersionManagementButtons from "components/VersionManagementButtons";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetMembersByCommunityIdQuery,
  useInviteMemberMutation,
  useUpdateMemberByIdMutation,
} from "state/members/membersApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";

// Sections
import ListOwnerships from "pages/ManageOwnerships/List";
import AddressInfo from "./Address";
import GeneralInfo from "./BasicInfo";

function SingleMember() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["members"]);

  // GET member
  const { id } = useParams();
  const { member } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ member: data?.entities[id] }),
    }
  );

  // UPDATE member
  const [updateMember, { isLoading: isLoadingU }] = useUpdateMemberByIdMutation();

  // INVITE member
  const [inviteMember, { isLoading: isInviting }] = useInviteMemberMutation();
  const onInvite = async () => {
    try {
      await inviteMember({ communityId: community.id, memberId: id }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("single_member.updated_title"),
          description: t("single_member.general.invite_sent"),
        })
      );
    } catch {
      dispatch(
        openNotificationPopup({
          notification: t("single_member.general.invite_error"),
          type: "error",
        })
      );
    }
  };
  const onUpdateMember = async (data, message) => {
    if (!member?.start_date || isLoadingU) return false;
    try {
      // Only send what we're changing
      const newData = Object.keys(data).reduce((acc, key) => {
        if (data[key] !== member[key]) {
          acc[key] = data[key];
        }
        return acc;
      }, {});
      await updateMember({
        communityId: community.id,
        memberId: id,
        data: {
          ...newData,
          start_date: member.start_date,
          end_date: member.end_date ?? null,
        },
      }).unwrap();

      dispatch(
        openSuccessAlert({
          title: t("single_member.updated_title"),
          description: message,
        })
      );
      return true;
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
      return false;
    }
  };

  const navItems = [
    { id: "general", icon: "person", label: t("single_member.general.title") },
    { id: "address", icon: "location_on", label: t("single_member.address.title") },
    { id: "ownerships", icon: "settings", label: t("single_member.ownerships.title") },
  ];

  const backToMembers = () => navigate(`/communities/${community.id}/members`);
  const hasDashboardAccess = member?.has_dashboard_access === true;

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="member-edit" />
      <div className="rec-crumbs">
        <Icon name="home" />
        <span>{community?.name || "—"}</span>
        <Icon name="chevron_right" />
        <button
          type="button"
          className="bg-transparent border-0 p-0 cursor-pointer text-inherit font-sans text-[12.5px]"
          onClick={backToMembers}
        >
          {t("title")}
        </button>
        <Icon name="chevron_right" />
        <b>{member?.name || "—"}</b>
      </div>

      <div className="rec-page-head">
        <Button variant="ghost" size="sm" onClick={backToMembers}>
          <Icon name="arrow_back" />
          {t("single_member.back_button")}
        </Button>
        {member && (
          <VersionManagementButtons
            communityId={community.id}
            entityType="membership"
            entityId={id}
            label={member.name}
            returnTo={`/communities/${community.id}/members/${id}`}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] items-start gap-rec">
        <AnchorNav items={navItems} />

        <div className="flex flex-col gap-rec min-w-0">
          {/* hero */}
          {member && (
            <div className="rec-card rec-card-pad">
              <div className="rec-profile-hero">
                <div className="rec-av-lg" style={{ background: avColor(member.name) }}>
                  {initials(member.name)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-[10px] flex-wrap">
                    <h2 className="m-0 text-[21px] font-bold text-ink tracking-[-0.3px]">
                      {member.name}
                    </h2>
                    {member.status === "ACTIVE" ? (
                      <Chip tone="green">{t("table.badges.active")}</Chip>
                    ) : (
                      <Chip tone="red">{t("table.badges.inactive")}</Chip>
                    )}
                    {member.is_host && <Chip tone="teal">{t("table.columns.is_host")}</Chip>}
                    {member.is_promoter && (
                      <Chip tone="amber">{t("table.columns.is_promoter")}</Chip>
                    )}
                  </div>
                  <div className="mt-[6px] text-ink-3 text-[13.5px] flex gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-[6px]">
                      <Icon name="mail" style={{ fontSize: 16 }} />
                      {member.email || "—"}
                    </span>
                    <span className="inline-flex items-center gap-[6px]">
                      <Icon name="badge" style={{ fontSize: 16 }} />
                      {`NIF ${member.nif || "—"}`}
                    </span>
                  </div>
                </div>
                {!hasDashboardAccess && (
                  <Button onClick={onInvite} disabled={isInviting}>
                    <Icon name="mail" />
                    {t("single_member.general.invite_button")}
                  </Button>
                )}
              </div>
            </div>
          )}

          {member && (
            <GeneralInfo
              data={{
                id,
                name: member.name,
                email: member.email,
                phone: member.phone,
                nif: member.nif,
                status: member.status,
                is_host: member.is_host ?? false,
                is_promoter: member.is_promoter ?? false,
                has_dashboard_access: member.has_dashboard_access ?? false,
              }}
              onUpdate={onUpdateMember}
              isLoading={isLoadingU}
            />
          )}
          {member && (
            <AddressInfo
              data={{ id, address: member.address }}
              onUpdate={onUpdateMember}
              isLoading={isLoadingU}
            />
          )}
          {member && <ListOwnerships data={member.ownerships} />}
        </div>
      </div>
    </div>
  );
}

export default SingleMember;
